/**
 * Parse money typed in staging forms (spaces, $, fr decimal comma).
 * @param {unknown} s
 * @returns {number}
 */
export function parseMoneyLoose(s) {
  if (s == null) return NaN;
  let t = String(s).trim().replace(/\s/g, '').replace(/\u00a0/g, '').replace(/\$/g, '');
  if (!t) return NaN;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  if (lastComma > lastDot) {
    t = t.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    t = t.replace(/,/g, '');
  } else if (lastComma !== -1) {
    t = t.replace(',', '.');
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Parse annual rate % from staging input.
 * @param {unknown} s
 * @returns {number}
 */
export function parsePercentLoose(s) {
  if (s == null) return NaN;
  let t = String(s).trim().replace(/\s/g, '').replace(/%/g, '');
  if (!t) return NaN;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  if (lastComma > lastDot) {
    t = t.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    t = t.replace(/,/g, '');
  } else if (lastComma !== -1) {
    t = t.replace(',', '.');
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * @param {'fr' | 'en' | string} language
 * @returns {string} BCP 47 locale
 */
function localeForLanguage(language) {
  return language === 'fr' ? 'fr-CA' : 'en-CA';
}

/** Round to cents (avoids float noise when saving / displaying). */
export function roundMoneyToCents(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return NaN;
  return Math.round(Math.max(0, n) * 100) / 100;
}

/**
 * CAD amount for staging (dollars + cents, up to 2 decimal places).
 * @param {number} n
 * @param {'fr' | 'en' | string} language
 */
export function formatStagingPrincipal(n, language) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const v = roundMoneyToCents(n);
  if (!Number.isFinite(v)) return '—';
  const loc = localeForLanguage(language);
  const num = new Intl.NumberFormat(loc, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
  return `${num}\u00a0$`;
}

/**
 * @param {number} n
 * @param {'fr' | 'en' | string} language
 */
export function formatStagingPercent(n, language) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const loc = localeForLanguage(language);
  const num = new Intl.NumberFormat(loc, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(n);
  return `${num}\u00a0%`;
}

/**
 * Plain numeric string for editing (focus), from current field value.
 * @param {string} current
 * @returns {string}
 */
export function moneyFieldEditValue(current) {
  const n = parseMoneyLoose(current);
  if (!Number.isFinite(n)) return current || '';
  const cents = roundMoneyToCents(n);
  if (!Number.isFinite(cents)) return current || '';
  if (Number.isInteger(cents)) return String(cents);
  return String(cents);
}

/**
 * @param {string} current
 * @returns {string}
 */
export function percentFieldEditValue(current) {
  const n = parsePercentLoose(current);
  if (!Number.isFinite(n)) return current || '';
  return String(n);
}
